<template>
  <v-container>
    <p class="secondary-block">
      Sélectionnez une version pour afficher le détail de celle-ci. La version
      sélectionnée visible est indiquée avec la couleur
      <span class="primary--text">verte</span>. Les requêtes rejetées apparaissent en
      <span class="error--text">rouge</span>. Les requêtes en cours apparaissent en
      <span class="secondary--text">bleu</span>.
    </p>
    <div class="font-weight-bold ml-2 mb-2">Aujourd'hui</div>
    <v-timeline dense>
      <v-timeline-item
        v-for="(evenement, key) in evenementsAssociation"
        :key="key"
        :color="color(evenement, key)"
      >
        <template #icon>
          <v-btn
            data-testid="selectionner-version"
            v-if="evenement.etatAssociation"
            icon
            @click="selectVersion(key)"
            class="white--text"
          >
            {{ evenement.ordre + 1 }}
          </v-btn>
        </template>
        <div class="d-flex align-center">
          <div class="text-caption mr-2 flex-grow-1">
            <div class="d-flex align-center">
              <div>
                <template v-if="evenement.etatAssociation">
                  <template v-if="estRadieOfficeSimple(evenement)">
                    <v-chip class="mr-2" label color="warning"> Radiée d'office </v-chip>
                  </template>
                  <template v-else-if="estRectifieOffice(evenement)">
                    <v-chip class="mr-2" label color="warning">
                      Rectifiée d'office
                    </v-chip>
                  </template>
                </template>
                <template v-else-if="isRejetee(evenement)">
                  <v-chip class="mr-2" label color="accent"> Rejetée </v-chip>
                </template>
                <template v-else>
                  <v-chip class="mr-2" label color="secondary"> En cours </v-chip>
                </template>
              </div>

              <div class="flex-grow-1">
                <div v-if="evenement.requete">
                  <a-issue-teleservice-icon v-if="evenement.requete.issueTeleservice" />
                  Requête
                  <template v-if="depot && lienRequeteNavigable(evenement)">
                    <router-link :to="toDetailRequete(evenement.requete.numero)">
                      {{ evenement.requete.numero }}
                    </router-link>
                  </template>
                  <template v-else>
                    {{ evenement.requete.numero }}
                  </template>
                </div>

                <div>
                  <v-chip
                    v-if="evenement.typeRequete"
                    small
                    color="info"
                    class="mr-2 mb-2 text-small"
                  >
                    {{ evenement.typeRequete | typerequete }}
                  </v-chip>
                  <template v-if="evenement.sousTypes?.length">
                    <v-chip
                      v-for="(sousType, key) in evenement.sousTypes"
                      :key="key"
                      small
                      color="tertiary"
                      class="mr-2 mb-2 text-small"
                    >
                      {{ sousType | soustyperequete }}
                    </v-chip>
                  </template>
                </div>
              </div>
            </div>

            <v-expansion-panels
              v-if="evenement.etatAssociation && evenement.type !== 'RADIATION_OFFICE'"
              flat
            >
              <v-expansion-panel :readonly="!avecExpansionPanel(evenement)">
                <v-expansion-panel-header
                  :hide-actions="!avecExpansionPanel(evenement)"
                  ripple
                >
                  <span
                    :class="{
                      'text-decoration-line-through': estRadieOffice(evenement),
                      'text-decoration-underline': !estRadieOffice(evenement),
                    }"
                  >
                    {{ evenement.type | typeevenement }}
                  </span>
                </v-expansion-panel-header>
                <v-expansion-panel-content v-if="avecExpansionPanel(evenement)">
                  <div
                    :class="{ 'text-decoration-line-through': estRadieOffice(evenement) }"
                  >
                    <div v-if="evenement.extrait" v-html="evenement.extrait" />
                    <div v-if="isEvenementAjoutDocument(evenement)">
                      Ajout du document :
                      <a-document-span
                        :value="evenement.evenement.ajoutDocument.document"
                      />
                    </div>
                    <div v-else-if="isEvenementSuppressionDocument(evenement)">
                      Suppression du document :
                      <a-document-span
                        :value="evenement.evenement.suppressionDocument.document"
                      />
                    </div>
                    <div v-else-if="isEvenementModificationDocument(evenement)">
                      {{ typeLibelleModificationDocument(evenement) }} du document :
                      <a-document-span
                        :value="evenement.evenement.modificationDocument.document"
                      />
                      <ul class="mt-2 mb2">
                        <li
                          v-for="c in modificationsEvenementDocument(evenement)"
                          :key="c.nom"
                        >
                          {{ c.nom }} :
                          <span class="text-decoration-line-through">{{ c.ancien }}</span>
                          <v-icon>mdi-arrow-right</v-icon>
                          <em>{{ c.nouveau }}</em>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div v-if="estRectifieOffice(evenement)" class="mt-2">
                    <p
                      v-for="(r, i) of requetesRectificationsOffice(evenement)"
                      :key="`r${i}`"
                    >
                      {{ formatRequeteRectificationOffice(r) }}
                    </p>
                  </div>
                  <div
                    v-if="estRadieOffice(evenement)"
                    class="mt-2"
                    v-html="formatRequeteRadiationOffice(evenement)"
                  ></div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <template v-if="isEvenementCreationTransfert(evenement)">
              Association d'origine
              <router-link
                :to="toDetailAssociation(evenement.evenement.transfert.numero)"
              >
                {{ evenement.evenement.transfert.numero }}
              </router-link>
            </template>

            <template v-if="isEvenementFusionAbsorption(evenement)">
              Association(s) absorbée(s) :
              <ul class="absorbees">
                <li
                  v-for="(item, index) in evenement.evenement.fusionnees.numeros"
                  :key="`association${index}`"
                >
                  <router-link :to="toDetailAssociation(item)">
                    {{ item }}
                  </router-link>
                </li>
              </ul>
            </template>
          </div>
          <div class="text-right">
            <template v-if="evenement.etatAssociation">
              <p>
                <template v-if="evenement.etatAssociation === 'RADIEE_OFFICE'">
                  <v-chip
                    v-if="key === evenementsAssociation.length - 1"
                    data-testid="selectionner-version-chip"
                    @click="selectVersion(key)"
                    label
                    color="primary"
                  >
                    {{ evenement.etatAssociation | etatassociation }}
                  </v-chip>
                </template>
                <v-chip
                  v-else-if="!estRadieOffice(evenement)"
                  data-testid="selectionner-version-chip"
                  @click="selectVersion(key)"
                  label
                  color="primary"
                  :outlined="estRadieOffice(evenement)"
                >
                  {{ evenement.etatAssociation | etatassociation }}
                  <template v-if="estRectifieOffice(evenement)">
                    &nbsp;rectifiée d'office
                  </template>
                </v-chip>
              </p>
              <strong>{{ evenement.dateCreation | date }}</strong>
            </template>
          </div>
        </div>
      </v-timeline-item>
    </v-timeline>
  </v-container>
</template>

<script>
import { toDetailRequete } from "@/routes/requetes";
import { toDetailAssociation } from "@/routes/associations";
import formatDate from "@/filters/date";

export default {
  props: {
    evenementsAssociation: {
      type: Array,
      required: false,
    },
    itemKey: {
      type: Number,
      required: false,
    },
    depot: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    premiereVersion() {
      if (this.evenementsAssociation && this.evenementsAssociation.length) {
        return this.evenementsAssociation[this.evenementsAssociation.length - 1];
      }
      return null;
    },
    premiereVersionRadieeOffice() {
      const premier = this.premiereVersion;

      return "RADIEE_OFFICE" === premier?.etatAssociation;
    },
  },
  methods: {
    toDetailRequete,
    toDetailAssociation,
    lienRequeteNavigable(evenement) {
      return (
        evenement.requete?.type !== "RADIATION_OFFICE" && !this.estRadieOffice(evenement)
      );
    },
    selectVersion(key) {
      this.$emit("select", key);
    },
    color(evenement, index) {
      if (this.itemKey === index) {
        return "primary";
      }

      if (this.estRadieOffice(evenement) || evenement.type === "RADIATION_OFFICE") {
        return "warning";
      }

      if (!evenement.etatAssociation && this.isRejetee(evenement)) {
        return "error";
      }

      return "grey";
    },
    isRejetee(evenement) {
      return !evenement.etatAssociation && evenement?.requete?.resultat === "REJETEE";
    },
    isEvenementDocument(evenement) {
      return (
        this.isEvenementAjoutDocument(evenement) ||
        this.isEvenementSuppressionDocument(evenement) ||
        this.isEvenementModificationDocument(evenement)
      );
    },
    isEvenementAjoutDocument(evenement) {
      return !!evenement.evenement?.ajoutDocument;
    },
    isEvenementSuppressionDocument(evenement) {
      return !!evenement.evenement?.suppressionDocument;
    },
    isEvenementModificationDocument(evenement) {
      return !!evenement.evenement?.modificationDocument;
    },
    formatRequeteRadiationOffice(evenement) {
      const req = this.requeteRadiationOffice(evenement);
      if (!req) return null;
      const premiereVersionRadieeOffice = this.premiereVersionRadieeOffice;

      let more =
        "De ce fait, l'association est rétablie à l'état qui était le sien antérieurement.";
      if (premiereVersionRadieeOffice) {
        if (evenement.ordre == 0)
          more = "De ce fait, l'association est radiée du registre.";
        else {
          const numeroRadiationOfficePremier = this.premiereVersion?.association
            ?.annotations?.radiationOffice?.numero;

          //on zappe les textes pour les evenements sans autre radiation d'office que la premiere version
          if (req.numero == numeroRadiationOfficePremier) return null;
        }
      }

      return `<p>Par ordonnance du ${formatDate(
        req.date
      )}, cette inscription a été radiée d'office conformément à l'article 29 de l'annexe du code de procédure civile.</p>
      <p class='q-mb-0'>
       ${more}
      </p>
      `;
    },
    formatRequeteRectificationOffice(req) {
      if (req.avecOrdonnance) {
        return `Par ordonnance du ${formatDate(
          req.dateOrdonnancement
        )}, cette inscription a été  rectifiée d'office conformément à l'article 6 de l'annexe du code de procédure civile.`;
      } else {
        return `Cette inscription a été rectifiée d'office le ${formatDate(
          req.dateOrdonnancement
        )} conformément à l'article 6 de l'annexe du code de procédure civile.`;
      }
    },
    requetesRectificationsOffice(evenement) {
      return evenement.association?.annotations?.rectifieePar;
    },
    requeteRadiationOffice(evenement) {
      return evenement.association?.annotations?.radiationOffice;
    },
    estRectifieOffice(evenement) {
      return !!this.requetesRectificationsOffice(evenement);
    },
    estRadieOfficeSimple(evenement) {
      return !!this.requeteRadiationOffice(evenement);
    },
    estRadieOffice(evenement) {
      return (
        this.estRadieOfficeSimple(evenement) ||
        !!evenement.association?.annotations?.associationRadieeOffice
      );
    },
    modificationsEvenementDocument(evenement) {
      const out = [];
      const modif = evenement.evenement.modificationDocument;
      const { journal, date } = modif;
      if (journal) {
        out.push({
          nom: "Journal d'annonces légales",
          ancien: journal.ancien,
          nouveau: journal.nouveau,
        });
      }
      if (date) {
        out.push({
          nom: "Date",
          ancien: formatDate(date.ancien),
          nouveau: formatDate(date.nouveau),
        });
      }
      return out;
    },
    typeLibelleModificationDocument(evenement) {
      const modif = evenement.evenement.modificationDocument;
      if (modif.activation) {
        return modif.activation.nouveau ? "Activation" : "Désactivation";
      }
      return "Modification";
    },
    isEvenementCreationTransfert(evenement) {
      return !!evenement.evenement?.transfert;
    },
    isEvenementFusionAbsorption(evenement) {
      return !!evenement.evenement?.fusionnees;
    },
    avecExpansionPanel(evenement) {
      return (
        evenement.extrait ||
        this.isEvenementDocument(evenement) ||
        this.estRectifieOffice(evenement) ||
        this.estRadieOffice(evenement)
      );
    },
  },
};
</script>

<style scoped>
ul.absorbees {
  display: inline;
  list-style: none;
  padding: 0px;
}

ul.absorbees li {
  display: inline;
}

ul.absorbees li:not(:last-child)::after {
  content: ", ";
}
</style>
